import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import { assetUrl } from 'utils/url'
import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import {
  resolveTugasPokokContent,
  resolveTugasTambahanContent,
  resolveTugasTambahanIds,
} from 'app/KinerjaGuruPage/utils/perencanaan'
import { IndikatorPerencanaanMultiSelect } from 'app/KinerjaShared/components/IndikatorPerencanaanMultiSelect'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { useUpsertPerencanaan } from 'app/KinerjaGuruPage/hooks/use-upsert-perencanaan'

import type { UpsertSKPPayload } from 'types/kinerja'
import type { OptionItem } from 'app/KinerjaShared/components/IndikatorPerencanaanMultiSelect'

type TugasPokokProps = {
  onNext: () => void
}

export function TugasPokok({ onNext }: TugasPokokProps) {
  const router = useRouter()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'SPF',
      }
    )

  // -----------------------------------------
  // Get content and options
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPF',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const {
    tugasTambahanOptions,
    tugasTambahanRequiredOptions,
    tugasTambahanRequiredIds,
    tugasPokokSelectableOptions,
    tugasPokokSelectableLabel,
    tugasPokokMandatoryOptions,
    tugasPokokSelectableOptionsMap,
    tugasTambahanOptionsMap,
  } = useMemo(() => {
    const { selectable, mandatory } = resolveTugasPokokContent(
      dataContentKinerjaUtama?.data?.kinerjaUtama || []
    )
    const { required, optional } = resolveTugasTambahanContent(
      dataContentKinerjaUtama?.data?.tugasTambahan || []
    )

    const tugasPokokSelectableOptions = selectable.map((item) => ({
      value: String(item.id),
      label: item.targetKualitas,
    }))
    const tugasTambahanRequiredOptions = required.map((item) => ({
      value: String(item.id),
      label: item.rencanaHasilKerja,
    }))
    const tugasTambahanOptions = optional.map((item) => ({
      value: String(item.id),
      label: item.rencanaHasilKerja,
    }))

    return {
      tugasPokokMandatoryOptions: mandatory.map((item) => ({
        value: String(item.id),
        label: item.rencanaHasilKerja,
      })),
      tugasTambahanRequiredIds: tugasTambahanRequiredOptions.map((item) =>
        Number(item.value)
      ),
      tugasTambahanRequiredOptions,
      tugasPokokSelectableOptions,
      tugasTambahanOptions,
      // Return options as Maps for constant time lookup
      tugasPokokSelectableOptionsMap: new Map(
        tugasPokokSelectableOptions.map((item) => [item.value, item])
      ),
      tugasTambahanOptionsMap: new Map(
        tugasTambahanOptions.map((item) => [item.value, item])
      ),
      // Since all selectable options will have same `rencanaHasilKinerja` value,
      // we can safely get the `rencanaHasilKerja` of first selectable options
      tugasPokokSelectableLabel: selectable[0]?.rencanaHasilKerja,
    }
  }, [dataContentKinerjaUtama])

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectedTugasPokok, setSelectedTugasPokok] = useState<
    Array<OptionItem>
  >([])
  const [selectedTugasTambahan, setSelectedTugasTambahan] = useState<
    Array<OptionItem>
  >([])
  const [isDirty, setIsDirty] = useState(false)

  const handleTugasPokokChange = (value: Array<OptionItem>) => {
    setSelectedTugasPokok(value)
    setIsDirty(true)
  }

  const handleTugasTambahanChange = (value: Array<OptionItem>) => {
    setSelectedTugasTambahan(value)
    setIsDirty(true)
  }

  // Sync with perencanaan data with the form
  useEffect(() => {
    // Sync tugas pokok
    if (perencanaanData?.data?.kinerjaUtama) {
      // Create a map of options for constant time lookup
      const currentTugasPokok = []

      for (const id of perencanaanData.data.kinerjaUtama) {
        const option = tugasPokokSelectableOptionsMap.get(String(id))
        if (option) {
          currentTugasPokok.push(option)
        }
      }

      setSelectedTugasPokok(currentTugasPokok)
    }

    // Sync tugas tambahan
    if (perencanaanData?.data?.tugasTambahan) {
      const { optionalIds } = resolveTugasTambahanIds(
        perencanaanData.data.tugasTambahan.map((item) => item.id),
        dataContentKinerjaUtama?.data?.tugasTambahan || []
      )
      const currentTugasTambahan = []

      for (const id of optionalIds) {
        const option = tugasTambahanOptionsMap.get(String(id))
        if (option) {
          currentTugasTambahan.push(option)
        }
      }

      setSelectedTugasTambahan(currentTugasTambahan)
    }
  }, [
    dataContentKinerjaUtama,
    perencanaanData,
    tugasTambahanOptionsMap,
    tugasPokokSelectableOptionsMap,
  ])

  const isValid = useMemo(
    () => selectedTugasPokok.length > 0,
    [selectedTugasPokok]
  )

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaan()

  const payload: UpsertSKPPayload = useMemo(
    () => {
      const tugasPokokMandatoryIds = tugasPokokMandatoryOptions.map((item) =>
        Number(item.value)
      )
      const tugasPokokSelectableIds = selectedTugasPokok.map((item) =>
        Number(item.value)
      )
      const kinerjaUtamaIds = tugasPokokMandatoryIds.concat(
        tugasPokokSelectableIds
      )

      const tugasTambahanIds = Array.from(
        new Set(
          tugasTambahanRequiredIds.concat(
            selectedTugasTambahan.map((item) => Number(item.value))
          )
        )
      )
      const tugasTambahan = tugasTambahanIds.map((id) => ({
        id,
        kuantitas: 1,
      }))

      return {
        id: perencanaanData?.data?.id,
        kinerjaUtama: kinerjaUtamaIds,
        tugasTambahan: tugasTambahan.length > 0 ? tugasTambahan : [],
        tipe: 'SPF',
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      perencanaanData,
      selectedTugasTambahan,
      selectedTugasPokok,
      tugasTambahanRequiredIds,
      tugasPokokMandatoryOptions,
    ]
  )

  const handleSaveToDraft = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      showSuccessMessage: true,
      onSuccess: () => {
        setIsDirty(false)
        router.push(`${PENGELOLAAN_KINERJA_GURU_PATH}`)
      },
    })

  const handleNext = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      onSuccess: onNext,
    })

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="flex items-center gap-3 mb-6">
          <img
            src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
            className="flex-grow-0 flex-shrink-0"
            alt=""
            width={64}
            height={64}
          />
          <div className="space-y-1">
            <Text as="h2" variant="heading-md">
              Pelaksanaan Tugas Pokok
            </Text>
            <Text color="subdued">
              Berikut tugas keseharian dan tugas tambahan Anda sebagai Guru di
              satuan pendidikan. Anda tidak perlu unggah Bukti Dukung.
            </Text>
          </div>
        </div>

        {isLoadingQueries ? (
          <div className="space-y-6">
            <Shimmer width="100%" height="124px" noOfLines={7} />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Mandatory Tugas Pokok */}
            {tugasPokokMandatoryOptions.map((item, index) => (
              <IndikatorPerencanaanPanel
                key={index}
                orderNumber={index + 1}
                title={item.label}
              />
            ))}

            {/* Selectable Tugas Pokok */}
            <IndikatorPerencanaanMultiSelect
              isLoading={isLoading}
              contents={{
                title: tugasPokokSelectableLabel,
                orderNumber: tugasPokokMandatoryOptions.length + 1,
              }}
              options={tugasPokokSelectableOptions}
              selected={selectedTugasPokok}
              onChange={handleTugasPokokChange}
            />

            {/* Tugas Tambahan */}
            <IndikatorPerencanaanMultiSelect
              isLoading={isLoading}
              contents={{
                label:
                  tugasTambahanRequiredOptions.length === 0
                    ? 'Opsional'
                    : undefined,
                title:
                  'Terlaksananya tugas tambahan dalam mendukung pembelajaran yang bermutu untuk semua',
                orderNumber: tugasPokokMandatoryOptions.length + 2,
                beforeOptions:
                  tugasTambahanRequiredOptions.length > 0 ? (
                    <div className="mb-3 space-y-3">
                      <>
                        <Text variant="body-sm" className="!text-blue-500">
                          Otomatis ditambahkan berdasarkan Kinerja Harian Anda
                        </Text>

                        {tugasTambahanRequiredOptions.map((item) => (
                          <div className="pr-24" key={item.value}>
                            <Text
                              className="block px-4 py-3 bg-white border rounded border-default"
                              as="div"
                            >
                              {item.label}
                            </Text>
                          </div>
                        ))}
                      </>

                      <Text variant="body-sm" className="!text-blue-500">
                        Opsional
                      </Text>
                    </div>
                  ) : undefined,
                afterOptions: (
                  <Text variant="body-sm" color="subdued" className="mt-2">
                    Pastikan pilihan Anda sesuai dengan yang ditugaskan.
                  </Text>
                ),
              }}
              options={tugasTambahanOptions}
              selected={selectedTugasTambahan}
              onChange={handleTugasTambahanChange}
            />
          </div>
        )}
      </div>
      <div className="flex justify-end gap-5 px-6 py-4 border-t border-subdued">
        <Button
          size="lg"
          color="white"
          disabled={isLoading || !isDirty}
          onClick={handleSaveToDraft}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={isLoading || !isValid}
          onClick={handleNext}
        >
          Ke Praktik Kinerja
        </Button>
      </div>
    </>
  )
}
